import { useState } from 'react';

import { useFetch } from '../../hooks';
import { getIntegrationInfo } from '../../api';
import { Form, FormItem } from '../../components';
import { Copy, Section, Input } from '../../components';
import type { IntegrationInfoType } from '../../api';

type IntegrationInfoProps = {
  campaignId: string;
};

const getCurlExample = (apiKey: string, campaignId?: string) => {
  if (!campaignId) return '';

  const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  return `curl -X POST "${baseUrl}/api/campaigns/${campaignId}/contacts" \\
  -H "Authorization: Bearer ${apiKey || '<API_KEY>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "contacts": [
    {
      "email": "john@acme.com",
      "first_name": "John",
      "last_name": "Doe",
      "company": "Acme Corp"
    }
  ],
  "on_duplicate": "skip"
}'`;
};

const IntegrationInfo: React.FC<IntegrationInfoProps> = ({ campaignId }) => {
  const { data } = useFetch<IntegrationInfoType>(getIntegrationInfo, []);
  const [showKey, setShowKey] = useState(false);

  const apiKey = data?.internal_service_token ?? '';
  const maskedKey = apiKey
    ? `${apiKey.slice(0, 4)}${'•'.repeat(Math.max(0, apiKey.length - 4))}`
    : '';

  const curlExample = getCurlExample(apiKey, campaignId);

  return (
    <Section
      title="Integration Info"
      subtitle="Use these values to configure external systems (N8N, Zapier, etc.) to call the import endpoint."
    >
      <div className="flex flex-col gap-y-md">
        <Form>
          <FormItem name="" label="API Key" inline={false}>
            <>
              <div className="flex flex-grow flex-row items-center gap-x-md">
                <Input value={(showKey ? apiKey : maskedKey) || ''} />
                <button
                  onClick={() => setShowKey(v => !v)}
                  className="text-small text-textLight hover:text-text cursor-pointer shrink-0"
                >
                  {showKey ? 'Hide' : 'Show'}
                </button>
                <Copy text={apiKey} label="Copy" />
              </div>
            </>
          </FormItem>
          <FormItem name="" label="Campaign ID" inline={false}>
            <div className="flex flex-grow flex-row items-center gap-x-md">
              <Input value={campaignId || '—  Select a campaign above'} />
              {campaignId && <Copy text={campaignId} label="Copy" />}
            </div>
          </FormItem>
          {!!campaignId && (
            <FormItem
              name=""
              label="Sample cURL Request"
              inline={false}
              labelRightSideContent={<Copy text={curlExample} label="Copy" />}
            >
              <>
                <Input multiline value={curlExample} rows={16} />
              </>
            </FormItem>
          )}
        </Form>
      </div>
    </Section>
  );
};

export default IntegrationInfo;
